import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserDetailsView, UserDetailsViewSkeleton } from "@surgia/ui";
import { useGetUser } from "../../hooks/useGetUser";
import { ADMIN_PROTECTED_ROUTES } from "../../routes";
import { formatDateWithMoment } from "@surgia/utils";

const UserDetails: React.FC = () => {
  const { userId, tenantId } = useParams<{
    userId: string;
    tenantId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const TARGET_COMPONENT_ID =
    import.meta.env.VITE_VITEAUTH_URL?.includes("test") ||
    import.meta.env.VITE_VITEAUTH_URL?.includes("develop")
      ? 8
      : 6;

  const {
    data: user,
    isLoading,
    error,
  } = useGetUser(
    {
      component_id: TARGET_COMPONENT_ID,
      tenant_id: parseInt(tenantId!),
      branches_ids: "", // Admin can see all branches
      user_id: userId!,
    },
    {
      enabled: !!(userId && tenantId),
    }
  );

  const handleGoBack = () => {
    navigate(
      ADMIN_PROTECTED_ROUTES.TENANT_DETAILS_USERS.replace(":id", tenantId!)
    );
  };

  if (isLoading) {
    return <UserDetailsViewSkeleton />;
  }

  if (error || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h3 className="text-lg font-medium text-red-600 mb-2">
            Error loading user details
          </h3>
          <p className="text-gray-500 mb-4">
            {error?.message || "User not found"}
          </p>
          <button
            onClick={handleGoBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back to Users
          </button>
        </div>
      </div>
    );
  }

  // Format user roles for display
  const userRoles = user.role?.map((role: any) => role.name).join(", ") || "-";

  // Format user branches for display
  const userBranchNames =
    user.branches?.map((branch: any) => branch.name).join(", ") || "-";

  return (
    <UserDetailsView
      breadcrumbItems={[
        {
          label: t("Tenants", "Tenants"),
          onClick: () => navigate(ADMIN_PROTECTED_ROUTES.TENANTS),
          show: true,
        },
        {
          label: t("Tenant Details", "Tenant Details"),
          onClick: () =>
            navigate(
              ADMIN_PROTECTED_ROUTES.TENANT_DETAILS.replace(":id", tenantId!)
            ),
          show: true,
        },
        {
          label: t("User details", "User details"),
          show: true,
        },
      ]}
      headerTitle={
        (user.full_name?.trim() && user.full_name.trim() !== "") ||
        (user?.first_name && user?.last_name)
          ? user.full_name?.trim() ||
            `${user.first_name || ""} ${user.last_name || ""}`.trim()
          : "Unknown User"
      }
      headerSubtitle={userId}
      accountDetails={{
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        phoneNumber: user.mobile,
        status: user.status,
        dateCreated: user.createdAt
          ? formatDateWithMoment(user.createdAt)
          : undefined,
        lastLogin: user.lastLogin
          ? formatDateWithMoment(
              user.lastLogin,
              "MMM DD, YYYY [at] HH:mm:ss A [UTC]"
            )
          : undefined,
        userId: userId,
      }}
      accessSettings={{
        role: userRoles,
        branch: userBranchNames,
      }}
      labels={{
        accountDetailsTitle: t("Account details", "Account details"),
        accessSettingsTitle: t("Access Settings", "Access Settings"),
        firstName: t("First name", "First name"),
        lastName: t("Last name", "Last name"),
        email: t("Email address", "Email address"),
        phoneNumber: t("Phone number", "Phone number"),
        status: t("Status", "Status"),
        dateCreated: t("Date created", "Date created"),
        lastLogin: t("Last login", "Last login"),
        role: t("Role", "Role"),
        branch: t("Branch", "Branch"),
      }}
    />
  );
};

export default UserDetails;
